import {
  Alert,
  AlertDescription,
  Badge,
  Button,
  Card,
  CardContent,
  Input,
  Label,
  toast,
} from '@sitewise/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';


import { fetchMe } from '../../../api/auth.js';
import {
  changePassword,
  totpConfirm,
  totpDisable,
  totpSetup,
  type TotpSetupResponse,
} from '../../../api/me.js';
import { ApiError } from '../../../lib/api-client.js';
import { useAuthStore } from '../../../stores/auth-store.js';

export function SecuritySettingsPage() {
  const queryClient = useQueryClient();
  const meQuery = useQuery({ queryKey: ['me'], queryFn: fetchMe });
  const totpEnabled = meQuery.data?.user.totpEnabled ?? false;

  return (
    <div className="space-y-6">
      <PasswordSection />
      <TotpSection
        totpEnabled={totpEnabled}
        onChanged={() => queryClient.invalidateQueries({ queryKey: ['me'] })}
      />
    </div>
  );
}

function PasswordSection() {
  const clearAuth = useAuthStore((s) => s.clear);
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [validationError, setValidationError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: () => changePassword({ currentPassword: current, newPassword: next }),
    onSuccess: () => {
      toast.success('Password changed. You will be signed out from all devices.');
      // The API revokes all sessions; force a reload so we land on /login.
      setTimeout(() => {
        clearAuth();
        window.location.assign('/login');
      }, 1500);
    },
  });

  return (
    <Card>
      <CardContent className="space-y-4 pt-6">
        <header>
          <h2 className="text-lg font-semibold">Password</h2>
          <p className="text-sm text-fg-secondary">
            Changing your password signs you out of every device.
          </p>
        </header>
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (next !== confirm) {
              setValidationError('Passwords do not match');
              return;
            }
            setValidationError(null);
            mutation.mutate();
          }}
          noValidate
        >
          <div className="space-y-1.5">
            <Label htmlFor="current-password">Current password</Label>
            <Input
              id="current-password"
              type="password"
              autoComplete="current-password"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              required
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="new-password">New password</Label>
            <Input
              id="new-password"
              type="password"
              autoComplete="new-password"
              value={next}
              onChange={(e) => setNext(e.target.value)}
              required
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="confirm-password">Confirm new password</Label>
            <Input
              id="confirm-password"
              type="password"
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              required
            />
          </div>
          {validationError ? (
            <Alert variant="danger">
              <AlertDescription>{validationError}</AlertDescription>
            </Alert>
          ) : null}
          {mutation.error ? (
            <Alert variant="danger">
              <AlertDescription>
                {mutation.error instanceof ApiError
                  ? mutation.error.message
                  : 'Could not change password.'}
              </AlertDescription>
            </Alert>
          ) : null}
          <div className="flex justify-end">
            <Button type="submit" disabled={mutation.isPending}>
              {mutation.isPending ? 'Updating…' : 'Change password'}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

interface TotpSectionProps {
  totpEnabled: boolean;
  onChanged: () => void;
}

function TotpSection({ totpEnabled, onChanged }: TotpSectionProps) {
  const [phase, setPhase] = useState<'idle' | 'setup' | 'disable'>('idle');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [setupResp, setSetupResp] = useState<TotpSetupResponse | null>(null);

  const setupMutation = useMutation({
    mutationFn: () => totpSetup(password),
    onSuccess: (resp) => {
      setSetupResp(resp);
      setCode('');
    },
  });

  const confirmMutation = useMutation({
    mutationFn: () => {
      if (!setupResp) throw new Error('No setup in progress');
      return totpConfirm({ secret: setupResp.secret, code });
    },
    onSuccess: () => {
      toast.success('Two-factor authentication enabled');
      setPhase('idle');
      setSetupResp(null);
      setPassword('');
      setCode('');
      onChanged();
    },
  });

  const disableMutation = useMutation({
    mutationFn: () => totpDisable({ password, code }),
    onSuccess: () => {
      toast.success('Two-factor authentication disabled');
      setPhase('idle');
      setPassword('');
      setCode('');
      onChanged();
    },
  });

  return (
    <Card>
      <CardContent className="space-y-4 pt-6">
        <header className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-lg font-semibold">Two-factor authentication</h2>
            <p className="text-sm text-fg-secondary">
              Require a 6-digit code from an authenticator app on each sign-in.
            </p>
          </div>
          <Badge variant={totpEnabled ? 'success' : 'default'}>
            {totpEnabled ? 'Enabled' : 'Disabled'}
          </Badge>
        </header>

        {phase === 'idle' ? (
          <div className="flex justify-end">
            {totpEnabled ? (
              <Button variant="secondary" onClick={() => setPhase('disable')}>
                Disable two-factor
              </Button>
            ) : (
              <Button onClick={() => setPhase('setup')}>Enable two-factor</Button>
            )}
          </div>
        ) : phase === 'setup' ? (
          <SetupFlow
            setupResp={setupResp}
            password={password}
            setPassword={setPassword}
            code={code}
            setCode={setCode}
            onStart={() => setupMutation.mutate()}
            onConfirm={() => confirmMutation.mutate()}
            onCancel={() => {
              setPhase('idle');
              setSetupResp(null);
              setPassword('');
              setCode('');
            }}
            startPending={setupMutation.isPending}
            startError={setupMutation.error}
            confirmPending={confirmMutation.isPending}
            confirmError={confirmMutation.error}
          />
        ) : (
          <DisableFlow
            password={password}
            setPassword={setPassword}
            code={code}
            setCode={setCode}
            onConfirm={() => disableMutation.mutate()}
            onCancel={() => {
              setPhase('idle');
              setPassword('');
              setCode('');
            }}
            pending={disableMutation.isPending}
            error={disableMutation.error}
          />
        )}
      </CardContent>
    </Card>
  );
}

interface SetupFlowProps {
  setupResp: TotpSetupResponse | null;
  password: string;
  setPassword: (v: string) => void;
  code: string;
  setCode: (v: string) => void;
  onStart: () => void;
  onConfirm: () => void;
  onCancel: () => void;
  startPending: boolean;
  startError: unknown;
  confirmPending: boolean;
  confirmError: unknown;
}

function SetupFlow(props: SetupFlowProps) {
  if (!props.setupResp) {
    return (
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          props.onStart();
        }}
        noValidate
      >
        <div className="space-y-1.5">
          <Label htmlFor="totp-password">Confirm your password</Label>
          <Input
            id="totp-password"
            type="password"
            autoComplete="current-password"
            required
            value={props.password}
            onChange={(e) => props.setPassword(e.target.value)}
          />
        </div>
        {props.startError ? (
          <Alert variant="danger">
            <AlertDescription>
              {props.startError instanceof ApiError
                ? props.startError.message
                : 'Could not start setup.'}
            </AlertDescription>
          </Alert>
        ) : null}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={props.onCancel}>
            Cancel
          </Button>
          <Button type="submit" disabled={props.startPending}>
            {props.startPending ? 'Generating…' : 'Continue'}
          </Button>
        </div>
      </form>
    );
  }

  return (
    <form
      className="space-y-4"
      onSubmit={(e) => {
        e.preventDefault();
        props.onConfirm();
      }}
      noValidate
    >
      <div className="flex flex-col items-center gap-3">
        <img
          src={props.setupResp.qrDataUrl}
          alt="TOTP QR"
          className="h-40 w-40 rounded border border-border bg-white p-2"
        />
        <details className="w-full text-xs text-fg-secondary">
          <summary className="cursor-pointer text-fg-muted">Show secret manually</summary>
          <code className="mt-1 block break-all rounded bg-surface-muted p-2 text-fg">
            {props.setupResp.secret}
          </code>
        </details>
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="confirm-totp">Enter the 6-digit code</Label>
        <Input
          id="confirm-totp"
          type="text"
          inputMode="numeric"
          maxLength={6}
          required
          autoComplete="one-time-code"
          value={props.code}
          onChange={(e) => props.setCode(e.target.value.replace(/\D+/g, ''))}
        />
      </div>
      {props.confirmError ? (
        <Alert variant="danger">
          <AlertDescription>
            {props.confirmError instanceof ApiError
              ? props.confirmError.message
              : 'Could not confirm code.'}
          </AlertDescription>
        </Alert>
      ) : null}
      <div className="flex justify-end gap-2">
        <Button type="button" variant="ghost" onClick={props.onCancel}>
          Cancel
        </Button>
        <Button type="submit" disabled={props.confirmPending}>
          {props.confirmPending ? 'Enabling…' : 'Enable'}
        </Button>
      </div>
    </form>
  );
}

interface DisableFlowProps {
  password: string;
  setPassword: (v: string) => void;
  code: string;
  setCode: (v: string) => void;
  onConfirm: () => void;
  onCancel: () => void;
  pending: boolean;
  error: unknown;
}

function DisableFlow(props: DisableFlowProps) {
  return (
    <form
      className="space-y-4"
      onSubmit={(e) => {
        e.preventDefault();
        props.onConfirm();
      }}
      noValidate
    >
      <div className="space-y-1.5">
        <Label htmlFor="disable-password">Password</Label>
        <Input
          id="disable-password"
          type="password"
          autoComplete="current-password"
          required
          value={props.password}
          onChange={(e) => props.setPassword(e.target.value)}
        />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="disable-code">Current 6-digit code</Label>
        <Input
          id="disable-code"
          type="text"
          inputMode="numeric"
          maxLength={6}
          required
          value={props.code}
          onChange={(e) => props.setCode(e.target.value.replace(/\D+/g, ''))}
        />
      </div>
      {props.error ? (
        <Alert variant="danger">
          <AlertDescription>
            {props.error instanceof ApiError
              ? props.error.message
              : 'Could not disable two-factor.'}
          </AlertDescription>
        </Alert>
      ) : null}
      <div className="flex justify-end gap-2">
        <Button type="button" variant="ghost" onClick={props.onCancel}>
          Cancel
        </Button>
        <Button type="submit" variant="destructive" disabled={props.pending}>
          {props.pending ? 'Disabling…' : 'Disable two-factor'}
        </Button>
      </div>
    </form>
  );
}
